/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import ResponsiveAccordion from "@eisinc/App/Components/ResponsiveAccordion/ResponsiveAccordion";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    isPdp?: boolean;
    extendedStyles?: ProductQuantityBreakPricingStyles;
}

export interface ProductQuantityBreakPricingStyles {
    table?: DataTableProps;
    tableBody?: DataTableBodyProps;
    tableRow?: DataTableRowProps;
    breakQtyCell?: DataTableCellBaseProps;
    breakPriceCell?: DataTableCellBaseProps;
    title?: TypographyPresentationProps;
    wrapper?: InjectableCss;
}

export const productQuantityBreakPricingStyles: ProductQuantityBreakPricingStyles = {
    wrapper: {
        css: css`
            margin: 0 -20px -20px -20px;
            padding: 20px 20px;
            background-color: ${getColor("common.white")};
            border-bottom-left-radius: 20px;
            border-bottom-right-radius: 20px;
        `,
    },
    tableRow: {
        css: css`
            background: ${getColor("common.white")} !important;
            border-bottom: 1px solid ${getColor("common.ui2")};
        `,
    },
    title: {
        variant: "p",
        size: 14,
        weight: 600,
        lineHeight: "18px",
        css: css`
            letter-spacing: 0.3px;
            margin-bottom: 10px;
        `,
    },
    breakQtyCell: {
        alignX: "left",
        css: css`
            font-size: 13px;
            font-weight: 600;
            letter-spacing: 0.3px;
            color: ${getColor("common.accentContrast")};
            padding: 0 10px 0 0;
            height: 30px;
            vertical-align: middle;
        `,
    },
    breakPriceCell: {
        alignX: "left",
        css: css`
            font-size: 13px;
            letter-spacing: 0.3px;
            color: ${getColor("common.accentContrast")};
            padding: 0 0 0 10px;
            width: 100%;
            height: 30px;
            vertical-align: middle;
        `,
    },
};

const ProductQuantityBreakPricing: FC<OwnProps & HasProduct> = ({
    isPdp,
    product,
    productInfo: { pricing },
    extendedStyles,
}) => {
    const showPricing = useAppSelector(state => state.context.showPricing);
    const [styles] = React.useState(() => mergeToNew(productQuantityBreakPricingStyles, extendedStyles));
    if (
        !isPdp ||
        !product ||
        product.quoteRequired ||
        !pricing ||
        !pricing.unitRegularBreakPrices ||
        pricing.unitRegularBreakPrices.length < 2 ||
        !showPricing
    ) {
        return null;
    }

    const breaksMaxIndex = pricing.unitRegularBreakPrices.length - 1;
    const unitOfMeasure =
        product.unitOfMeasures?.find(o => o.unitOfMeasure === pricing.unitOfMeasure)?.description ||
        product.unitOfMeasures?.find(o => o.unitOfMeasure === pricing.unitOfMeasure)?.unitOfMeasureDisplay;

    const pricingUnit = product?.properties?.pricingUnit;

    return (
        <ResponsiveAccordion breakpoint="sm">
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.title}>{translate("Buy In Bulk & Save")}</Typography>
                <DataTable {...styles.table}>
                    <DataTableBody {...styles.tableBody}>
                        {pricing!.unitRegularBreakPrices!.map((breakPrice, index) => (
                            <DataTableRow
                                {...styles.tableRow}
                                key={breakPrice.breakQty}
                                data-test-selector="breakPriceRow"
                            >
                                <DataTableCell {...styles.breakQtyCell} data-test-selector="breakQty">
                                    {breakPrice.breakQty}
                                    {index < breaksMaxIndex
                                        ? `-${pricing!.unitRegularBreakPrices![index + 1].breakQty - 1}`
                                        : `+`}
                                </DataTableCell>
                                <DataTableCell {...styles.breakPriceCell} data-test-selector="price">
                                    {breakPrice.breakPriceDisplay}
                                    {unitOfMeasure && (
                                        <>
                                            &nbsp;/&nbsp;{pricingUnit || ""}
                                            {unitOfMeasure}
                                        </>
                                    )}
                                </DataTableCell>
                            </DataTableRow>
                        ))}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
        </ResponsiveAccordion>
    );
};

export default withProduct(ProductQuantityBreakPricing);
